<template>
  <div class="variant">
    <div class="image">
      <img :src="`https://${broadcast.fqdn}/images/h/40/${variant.image_url}`" />
    </div>
    <div class="information">
      <span class="id" v-if="variant.variant_id">{{ variant.variant_id }}</span>
      <h4>{{ variant.title }}</h4>
    </div>
    <div class="actions">
      <div class="display-time" v-if="videoTime.length > 0 && broadcast.status.life_cycle_status === 'complete'">
        <span v-for="time in videoTime" :key="time.timestamp" class="blue" @click.prevent.stop="$emit('gotoInVideo', time.time)">{{ time.timestamp }}</span>
      </div>
      <div class="display-product" @click.prevent.stop="displayVariant" v-if="['live', 'liveStopping'].includes(broadcast.status.life_cycle_status)">
        <i :class="`${ state === 'toggling' ? 'mdi mdi-loading mdi-spin' : !timestamps.at(-1) || timestamps.at(-1).hide_at ? 'icon-eye-24 blue' : 'icon-stop red'}`" />
      </div>
    </div>
  </div>
</template>

<script>

import { toHoursAndMinutes } from '@/utils';
import swal from 'sweetalert';

export default {
  props: {
    productID: { type: String, required: true },
    variant: { type: Object, required: true },
  },
  computed: {
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Event data
     */
    eventData() {
      return this.$store.getters['Products/eventProducts'].find(product => product.id === this.productID);
    },
    timestamps() {
      return this.eventData?.timestamps.filter(ts => ts.variant_id === this.variant.variant_id) || [];
    },
    /**
     * Return array of times with minut:second the variant was shown
     * @returns {Array}
     */
    videoTime() {
      if (!this.eventData) {
        return [];
      }

      const {
        broadcast: { actual_start_time },
        timestamps,
      } = this;

      const times = [];
      const y = new Date(actual_start_time);

      for (let i = 0; i < timestamps.length; i += 1) {
        let time = '';
        const x = new Date(timestamps[i].display_at);
        const displayTime = toHoursAndMinutes(Math.abs(x.getTime() - y.getTime()) / 1000);
        time += `${displayTime.h ? `${displayTime.h < 10 ? '0' : ''}${displayTime.h}:` : ''}${displayTime.m ? `${displayTime.m < 10 ? '0' : ''}${displayTime.m}:` : ''}${displayTime.s < 10 ? '0' : ''}${displayTime.s}`;
        if (timestamps[i].hide_at) {
          const z = new Date(timestamps[i].hide_at);
          const hideTime = toHoursAndMinutes(Math.abs(z.getTime() - y.getTime()) / 1000);
          time += `-${hideTime.h ? `${hideTime.h < 10 ? '0' : ''}${hideTime.h}:` : ''}${hideTime.m ? `${hideTime.m < 10 ? '0' : ''}${hideTime.m}:` : ''}${hideTime.s < 10 ? '0' : ''}${hideTime.s}`;
        }
        times.push({
          timestamp: time,
          time: Math.abs(x.getTime() - y.getTime()) / 1000,
          variant_id: timestamps[i].variant_id || null,
        });
      }

      return times;
    },
  },
  methods: {
    async displayVariant() {
      if (this.state === 'toggling') {
        return;
      }

      const socketState = this.$store.getters['Events/socketState'](this.broadcast.id);

      if (socketState !== 'connected') {
        swal({ title: 'Can\'t toggle product', text: 'Not connected to websocket. Please reload browser and try again.', icon: 'error' });
        return;
      }

      const current = this.timestamps.at(-1);
      const event = !current || current.hide_at ? 'product.show' : 'product.hide';

      this.state = 'toggling';

      await this.$store.dispatch('Products/toggleEventProduct', {
        id: this.broadcast.id,
        payload: {
          event,
          id: this.productID,
          variant_id: this.variant.variant_id,
        },
      });

      this.state = '';
    },
  },
  data() {
    return {
      state: '',
    };
  },
};
</script>

<style lang="scss" scoped>
.variant {
  background-color: white;
  border-radius: 5px;
  display: flex;
  flex: 1;
  width: 100%;
  overflow: hidden;
  transition: all .3s;

  .image {
    img {
      border-radius: 5px 0 0 5px;
      aspect-ratio: 1;
      object-fit: contain;
      height: 100%;
      width: 40px;
    }
  }

  .information {
    display: flex;
    overflow: hidden;
    flex-direction: column;
    justify-content: center;
    padding: 8px;

    span.id {
      font-family: 'Fira Mono', monospace;
      font-size: 10px;
      line-height: 12px;
      color: #727479;
    }

    h4, p {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    h4 {
      font-weight: 500;
      font-size: 13px;
      line-height: 18px;
    }
  }

  .actions {
    margin-left: auto;
    display: flex;

    .gray {
      color: var(--color-cta-gray);
      pointer-events: none;
    }

    .blue {
      color: var(--color-cta-blue);
    }

    .red {
      color: var(--color-cta-red);
    }

    & > * {
      border-left: 1px solid #EEF0F9;
    }

    .display-time {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0 8px;

      span {
        cursor: pointer;
        font-family: "Fira Mono", monospace;
        font-size: 10px;
        white-space: nowrap;
      }
    }

    .display-product {
      display: flex;
      & > * {
        cursor: pointer;
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: center;
        padding: 0 15px;
        font-size: 20px;
      }
    }
  }
}
</style>
